import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { bangkokNowMs } from '@/lib/batchAvailability'
import { barStyle, safeColor } from '@/lib/promoStyle'
import { copyCouponCode, setPendingCoupon } from '@/lib/promoCoupon'
import type { ActiveCampaign } from '@/services/campaignService'

const NAVBAR_H = 64   // Navbar เป็น fixed สูง h-16 — แถบแคมเปญวางต่อจากใต้ Navbar

// นับถอยหลัง — เวลาไทยจากนาฬิกาเครื่อง + ส่วนต่างกับเวลา server (clockOffsetMs) จึงตรงกับ server ไม่เพี้ยนตามเครื่องผู้ใช้
export function useCountdown(endsAt: string, clockOffsetMs: number) {
  const calc = () => new Date(endsAt).getTime() - (bangkokNowMs() + clockOffsetMs)
  const [left, setLeft] = useState(calc)
  useEffect(() => {
    setLeft(calc())
    const t = setInterval(() => setLeft(calc()), 1000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endsAt, clockOffsetMs])
  const total = Math.max(Math.floor(left / 1000), 0)
  return {
    expired: left <= 0,
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  }
}

function TimeBox({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex w-11 flex-col items-center rounded-md bg-white/15 py-1 text-white ring-1 ring-white/25 sm:w-14">
      <span className="text-lg font-bold leading-6 tabular-nums sm:text-2xl sm:leading-7">{String(value).padStart(2, '0')}</span>
      <span className="text-[9px] leading-3 text-white/80 sm:text-[10px]">{label}</span>
    </div>
  )
}

export function CodeChip({ code, color, onCopied }: { code: string; color: string; onCopied?: () => void }) {
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      onClick={async () => {
        if (await copyCouponCode(code)) {
          setDone(true)
          onCopied?.()
          setTimeout(() => setDone(false), 1600)
        }
      }}
      title="กดเพื่อคัดลอกรหัส"
      style={{ color }}
      className="group flex shrink-0 items-center gap-2 rounded-full border-2 border-dashed border-white/70 bg-white px-3.5 py-1.5 text-sm font-extrabold tracking-wide shadow-sm transition hover:scale-[1.03] sm:text-base"
    >
      {code}
      {done ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4 opacity-60 group-hover:opacity-100" />}
      <span className="sr-only">{done ? 'คัดลอกแล้ว' : 'คัดลอก'}</span>
    </button>
  )
}

// แถบแคมเปญใต้เมนู — ชื่อโปร + นับถอยหลัง + รหัสที่กดคัดลอกได้ · ไม่มีปุ่มปิด (แสดงตลอดช่วงแคมเปญ หายเองเมื่อหมดเวลา)
// ใส่ใน layout ต่อจาก <Navbar />: คอมโพเนนต์นี้จองพื้นที่ในหน้าเท่าความสูงแถบ เนื้อหาหน้าจึงถูกดันลงไม่โดนทับ
export default function PromoBanner({ campaign, clockOffsetMs }: { campaign: ActiveCampaign; clockOffsetMs: number }) {
  const banner = campaign.banner!
  const color = safeColor(campaign.color)
  const [copiedNote, setCopiedNote] = useState(false)
  const innerRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(56)
  const cd = useCountdown(campaign.ends_at, clockOffsetMs)

  // วัดความสูงจริงของแถบ (มือถือสูงกว่าเดสก์ท็อป) เพื่อจองพื้นที่ให้พอดี
  useLayoutEffect(() => {
    const el = innerRef.current
    if (!el) return
    const update = () => setHeight(el.offsetHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // ?coupon=CODE ใน URL → จำไว้กรอกให้ที่หน้าชำระเงิน
  useEffect(() => {
    const c = new URLSearchParams(window.location.search).get('coupon')
    if (c) setPendingCoupon(c)
  }, [])

  if (cd.expired) return null

  return (
    <div className="relative z-30" style={{ height }}>
      <div ref={innerRef} className="absolute left-0 right-0" style={{ top: NAVBAR_H }}>
        <div className="relative text-white" style={barStyle(color)}>
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 py-2.5 md:justify-between md:px-6">
            {/* ชื่อโปร */}
            <div className="text-center leading-none md:text-left">
              {banner.top && <p className="text-xs font-extrabold tracking-wider sm:text-base">{banner.top}</p>}
              <p className="mt-0.5 text-xl font-black tracking-tight sm:text-3xl">{banner.main}</p>
            </div>

            {/* นับถอยหลัง */}
            {campaign.show_countdown && (
              <div className="flex items-center gap-1.5 sm:gap-2" aria-label="เวลาที่เหลือของโปรโมชัน">
                {cd.days > 0 && <><TimeBox value={cd.days} label="วัน" /><span className="font-bold">:</span></>}
                <TimeBox value={cd.hours} label="ชั่วโมง" />
                <span className="font-bold">:</span>
                <TimeBox value={cd.minutes} label="นาที" />
                <span className="font-bold">:</span>
                <TimeBox value={cd.seconds} label="วินาที" />
              </div>
            )}

            {/* รหัส (ไม่มีคูปองที่ใช้ได้ = ไม่แสดงช่องนี้) */}
            {campaign.coupons.length > 0 && (
              <div className="flex max-w-full items-center gap-2 overflow-x-auto rounded-full bg-white/15 p-1.5 pl-3 ring-1 ring-white/25">
                <span className="shrink-0 text-xs font-semibold sm:text-sm">กรอกโค้ด</span>
                <div className="flex items-center gap-2">
                  {campaign.coupons.slice(0, 2).map((c) => (
                    <CodeChip key={c.code} code={c.code} color={color} onCopied={() => { setCopiedNote(true); setTimeout(() => setCopiedNote(false), 2200) }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ข้อความยืนยันการคัดลอก */}
          <div
            className={`pointer-events-none absolute bottom-1 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-3 py-0.5 text-[11px] font-medium transition-opacity ${copiedNote ? 'opacity-100' : 'opacity-0'}`}
            role="status"
          >
            คัดลอกรหัสแล้ว — ไปวางที่ช่องคูปองตอนชำระเงิน
          </div>

        </div>
      </div>
    </div>
  )
}
